/**
 * What's new for the signed-in coach: the updates, the state behind her menu
 * dot and NOVA Home card, and her last-seen time. Logic lives in
 * `~/lib/server/whats-new`.
 *
 * Aliases: whats new api, last seen, unseen updates
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import {
  type FeatureModule,
  featureModuleSchema,
} from "~/lib/shared/features/modules";

export const whatsNewQueryKeys = {
  list: (module: FeatureModule | undefined) =>
    ["whats-new", "list", module ?? "all"] as const,
  state: ["whats-new", "state"] as const,
};

export const getWhatsNewEntriesFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware, requireClerkCoachAuth])
  .validator(z.object({ module: featureModuleSchema.optional() }))
  .handler(async ({ data }) => {
    const { listPublishedWhatsNewEntries } =
      await import("~/lib/server/whats-new");
    return listPublishedWhatsNewEntries(data.module);
  });

export function whatsNewEntriesQueryOptions(module: FeatureModule | undefined) {
  return queryOptions({
    queryKey: whatsNewQueryKeys.list(module),
    queryFn: () => getWhatsNewEntriesFn({ data: { module } }),
  });
}

export const getMyWhatsNewStateFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware, requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { getWhatsNewState } = await import("~/lib/server/whats-new");
    return getWhatsNewState(context.user.id, new Date());
  });

export const myWhatsNewStateQueryOptions = queryOptions({
  queryKey: whatsNewQueryKeys.state,
  queryFn: () => getMyWhatsNewStateFn(),
});

export const markWhatsNewSeenFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { markWhatsNewSeen } = await import("~/lib/server/whats-new");
    await markWhatsNewSeen(context.user.id, new Date());
  });
